import React, { createContext, useContext, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Check, 
  X, 
  Info, 
  AlertTriangle,
  XCircle 
} from 'lucide-react';
import { cn } from '../../lib/utils';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning' | 'loading';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastProps {
  id: string;
  title: string;
  message?: string;
  variant?: ToastVariant;
  duration?: number;
  closable?: boolean;
  onClose?: () => void;
  action?: ToastAction;
}

interface ToastContextValue {
  toasts: ToastProps[];
  showToast: (toast: Omit<ToastProps, 'id'>) => string;
  hideToast: (id: string) => void;
  hideAllToasts: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const variantConfig = {
  success: {
    icon: Check,
    className: 'border border-border/50 bg-card/90 backdrop-blur-md text-foreground border-l-2 border-l-success',
    iconClassName: 'text-success'
  },
  error: {
    icon: XCircle,
    className: 'border border-border/50 bg-card/90 backdrop-blur-md text-foreground border-l-2 border-l-destructive',
    iconClassName: 'text-destructive'
  },
  info: {
    icon: Info,
    className: 'border border-border/50 bg-card/90 backdrop-blur-md text-foreground border-l-2 border-l-info',
    iconClassName: 'text-info'
  },
  warning: {
    icon: AlertTriangle,
    className: 'border border-border/50 bg-card/90 backdrop-blur-md text-foreground border-l-2 border-l-warning',
    iconClassName: 'text-warning'
  },
  loading: {
    icon: null,
    className: 'border border-border/50 bg-card/90 backdrop-blur-md text-foreground border-l-2 border-l-muted-foreground',
    iconClassName: 'text-muted-foreground'
  }
};

function Toast({ id, title, message, variant = 'info', closable = true, onClose, action }: ToastProps) {
  const { hideToast } = useToast();
  const config = variantConfig[variant];
  const Icon = config.icon;

  const handleClose = () => {
    hideToast(id);
    onClose?.();
  };

  const handleAction = () => {
    action?.onClick();
    hideToast(id);
  };

  return (
    <motion.div
      layout
      role={variant === 'error' || variant === 'warning' ? 'alert' : 'status'}
      initial={{ opacity: 0, y: -8, x: 8 }}
      animate={{ opacity: 1, y: 0, x: 0 }}
      exit={{ opacity: 0, y: -4, x: 4, transition: { duration: 0.15 } }}
      transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        'pointer-events-auto mb-2 flex w-full max-w-[360px] items-start gap-2 rounded-lg p-2.5',
        config.className
      )}
      style={{
        boxShadow: '0 0 0 1px rgba(0,0,0,.03), 0 2px 4px rgba(0,0,0,.04), 0 6px 12px rgba(0,0,0,.06), 0 12px 24px rgba(0,0,0,.08)'
      }}
    >
      {variant === 'loading' ? (
        <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
      ) : Icon && (
        <Icon className={cn('h-3.5 w-3.5 shrink-0 mt-0.5', config.iconClassName)} />
      )}

      <div className="flex-1 min-w-0">
        <p className="text-13 font-medium leading-tight">{title}</p>
        {message && (
          <p className="mt-1 text-11 text-muted-foreground leading-relaxed">{message}</p>
        )}
        {action && (
          <button
            onClick={handleAction}
            className="mt-1.5 text-11 font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm"
          >
            {action.label}
          </button>
        )}
      </div>

      {closable && (
        <button
          onClick={handleClose}
          aria-label="Close"
          className="shrink-0 rounded-sm opacity-40 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </motion.div>
  );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  const showToast = useCallback((toast: Omit<ToastProps, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast = { ...toast, id };

    setToasts(prev => {
      // Max 3 toasts: if we already have 3, remove the oldest one
      const updated = prev.length >= 3 ? prev.slice(1) : prev;
      return [...updated, newToast];
    });

    if (toast.duration !== 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, toast.duration || 3000);
    }

    return id;
  }, []);

  const hideToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const hideAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, showToast, hideToast, hideAllToasts }}>
      {children}
      <div
        aria-live="polite"
        aria-relevant="additions"
        className="pointer-events-none fixed inset-0 z-[9999] flex flex-col items-center justify-end p-4 sm:items-end sm:justify-start sm:p-4"
      >
        <AnimatePresence>
          {toasts.map(toast => (
            <Toast key={toast.id} {...toast} />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}